import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import CampaignScopeToolbar from '../../components/crm/CampaignScopeToolbar';
import CallbackRemindersCalendar from '../../components/crm/CallbackRemindersCalendar';
import ProspectDetailModal from '../../components/ProspectDetailModal';
import PipelineMetricCard from './components/PipelineMetricCard';
import PipelineStageFlow from './components/PipelineStageFlow';
import { fetchProspects, apiFetch } from '../../api/prospects';
import { fetchCampaigns } from '../../api/campaigns';
import {
  buildPipelineMetricsFromStageData,
  computeStageDataFromProspects,
  remindersInMonth,
} from '../../utils/pipelineStats';

const ProspectPipelineMonitor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [campaignId, setCampaignId] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [pipelineMetrics, setPipelineMetrics] = useState([]);
  const [pipelineStageData, setPipelineStageData] = useState({
    lead: 0,
    qualified: 0,
    proposal: 0,
    negotiation: 0,
    closed: 0,
  });
  const [monthReminders, setMonthReminders] = useState([]);
  const [selectedProspectId, setSelectedProspectId] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCampaigns(true);
      try {
        const res = await fetchCampaigns();
        if (!cancelled) setCampaigns(res?.data?.campaigns || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoadingCampaigns(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadPipeline = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const meRes = await apiFetch('/me');
      const user = meRes?.data?.user;
      const admin = user?.permission === 'admin';
      const prospectFilters = {
        limit: 200,
        ...(campaignId ? { campaignId } : {}),
        ...(!admin && user?._id ? { assignedTo: user._id } : {}),
      };

      const calendarFilters = {
        limit: 200,
        ...(user?._id ? { assignedTo: user._id } : {}),
      };

      const [prospectsRes, calendarRes] = await Promise.all([
        fetchProspects(prospectFilters),
        fetchProspects(calendarFilters),
      ]);

      const prospects = prospectsRes?.data?.prospects || [];
      const calendarProspects = calendarRes?.data?.prospects || [];

      const stageData = computeStageDataFromProspects(prospects);
      setPipelineStageData(stageData);
      setPipelineMetrics(buildPipelineMetricsFromStageData(stageData));

      const now = new Date();
      setMonthReminders(
        remindersInMonth(calendarProspects, now.getFullYear(), now.getMonth()),
      );
    } catch (e) {
      console.error(e);
      setError('Impossible de charger les données du pipeline.');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    const guard = async () => {
      try {
        await loadPipeline();
      } catch (e) {
        console.error(e);
        navigate('/login', { replace: true });
      }
    };
    guard();
  }, [loadPipeline, navigate]);

  const campaignName = useMemo(() => {
    if (!campaignId) return null;
    const c = campaigns.find((x) => String(x._id || x.id) === String(campaignId));
    return c?.name || null;
  }, [campaignId, campaigns]);

  return (
    <>
      <Helmet>
        <title>Tableau de bord - CRM</title>
        <meta
          name="description"
          content="Suivez votre pipeline par campagne et vos rappels du mois."
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main className="main-content mx-auto max-w-7xl pb-12">
          <div className="mb-6 md:mb-8">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">Activité commerciale</p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
              Tableau de bord
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              {campaignName
                ? `Campagne « ${campaignName} » — étapes du cycle et rappels du mois.`
                : 'Répartition par étape de vente et rappels planifiés ce mois-ci.'}
            </p>
          </div>

          <CampaignScopeToolbar
            className="mb-8"
            campaigns={campaigns}
            campaignId={campaignId}
            onCampaignChange={setCampaignId}
            loadingCampaigns={loadingCampaigns}
          />

          {error ? (
            <p className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              {error}
            </p>
          ) : null}

          {loading ? (
            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-28 animate-pulse rounded-xl border border-border bg-muted/40"
                />
              ))}
            </div>
          ) : (
            <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {pipelineMetrics.map((metric, index) => (
                <PipelineMetricCard key={index} {...metric} />
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
            <div className="xl:col-span-8">
              {loading ? (
                <div className="h-80 animate-pulse rounded-2xl border border-border bg-muted/30" />
              ) : (
                <PipelineStageFlow data={pipelineStageData} />
              )}
            </div>
            <div className="xl:col-span-4">
              <CallbackRemindersCalendar
                className="h-full"
                reminders={monthReminders}
                onProspectClick={(id) => id && setSelectedProspectId(id)}
              />
            </div>
          </div>
        </main>
      </div>

      {selectedProspectId ? (
        <ProspectDetailModal
          prospectId={selectedProspectId}
          onClose={() => setSelectedProspectId(null)}
        />
      ) : null}
    </>
  );
};

export default ProspectPipelineMonitor;
